/**
 * LES TEXTES DU MODULE DES ALARMES V2, EN FRANÇAIS.
 * ============================================================================
 *
 * Source : la maquette `surveillance.html` du dossier v3.2 (dépôt `top`,
 * `docs/projects/alarmes/maquettes/`), citée par sa ligne `S:<ligne>` dans le
 * commentaire de chaque clé, et le lexique v1.1 (`lexique.json`, copié ici).
 * Un texte absent de la maquette porte `[PROPOSITION]` et se relit en revue.
 *
 * Règles tenues par `textes.spec.ts` : aucun mot interne du lexique, aucun nom
 * de marque. Fusionné sous `alarmesV2.*` au chargement des routes du module
 * (`enregistrer.ts`). Syntaxe de vue-i18n : `{x}` est une valeur à insérer ;
 * aucun autre caractère spécial n'est employé.
 */
export const MESSAGES_ALARMES_V2 = {
    /** Le titre de l'écran 1 (S:1373). */
    titre: "Surveillance",

    liste: {
        /** S:1375, réduit à ce que la liste fait en lecture. [PROPOSITION] */
        intro: "La liste des surveillances de l'entreprise active, déjà triée : les alarmes à traiter d'abord, celles pour information ensuite, repliées sous leur titre.",
        /** S:1381 */
        recherche: "Rechercher",
        /** S:1382 */
        rechercheAide: "Équipement, lieu ou type d'alarme",
        /** S:1385 */
        filtreEtat: "État",
        filtres: {
            /** S:1387-1394 */
            tous: "Tous les états",
            a_traiter: "Actives ou non acquittées",
            declenchee: "Active, non acquittée",
            prise_en_compte: "Active, acquittée",
            retour_non_vu: "Revenue à la normale, non acquittée",
            armee: "En service, normale",
            non_surveillee: "Défaut de mesure ou ne peut pas prévenir",
            desactivee: "Désactivée",
        },
        colonnes: {
            /** S:1428-1435, sans les colonnes des lots suivants. */
            etat: "État",
            equipement: "Équipement",
            type: "Type d'alarme",
            mesure: "Dernière mesure",
            dit: "Ce que dit l'état",
            /** S:1436, vide à l'écran ; lu par un lecteur d'écran. [PROPOSITION] */
            actions: "Actions",
        },
        /** S:5236-5237 */
        groupeInfoUne: "Pour information · 1 surveillance",
        groupeInfoPlusieurs: "Pour information · {n} surveillances",
        groupeInfoEnCours: ", dont {n} en cours",
        /** S:5238 */
        groupeInfoAide:
            "Elles préviennent par mail au début et à la fin, l'écart inhabituel au début seulement, sans acquittement, sans rappel, jamais de SMS par défaut.",
        /** S:5243 */
        aucunFiltre: "Aucune surveillance ne répond à ce filtre.",
        /** [PROPOSITION] l'entreprise n'a encore aucune surveillance. */
        vide: "Aucune surveillance pour le moment.",
        /** [PROPOSITION] */
        chargement: "Lecture des surveillances en cours.",
        /** Plan §2.3, pagination. [PROPOSITION] */
        suite: "Afficher la suite",
        /** [PROPOSITION] */
        suiteAide:
            "La lecture s'est arrêtée sur plusieurs pages sans surveillance visible pour vous.",
        /** S:5283, réduit au décompte. */
        piedUne: "{n} surveillance retenue par le filtre sur {total}.",
        piedPlusieurs: "{n} surveillances retenues par le filtre sur {total}.",
        /** Plan §2.3 et §7 : le type n'est pas au catalogue publié. [PROPOSITION] */
        typeNonDisponible: "Type d'alarme non disponible",
        /** [PROPOSITION] l'équipement n'est pas nommé dans la réponse. */
        equipementSansNom: "Équipement sans nom",
    },

    /** Les libellés d'état, dans l'ordre de `etatInfo` (S:5062-5125, S:2411-2418). */
    etats: {
        pourInformation: "Pour information, en cours",
        defautMesureNonAcquitte: "Défaut de mesure, non acquitté",
        activeNonAcquittee: "Active, non acquittée",
        activeAcquittee: "Active, acquittée",
        revenueNonAcquittee: "Revenue à la normale, non acquittée",
        enService: "En service",
        enAttente: "En attente",
        defautMesure: "Défaut de mesure",
        nePeutPasPrevenir: "Ne peut pas prévenir",
        desactivee: "Désactivée",
        /** Hors maquette : un état servi que l'écran ne sait pas lire. */
        inconnu: "État inconnu",
    },

    /** Les lignes de détail de `etatInfo` (S:5070-5125). */
    details: {
        /** Hors maquette : un état servi que l'écran ne sait pas lire. */
        etatInconnu: "Son état n'a pas pu être lu.",
        /** Hors maquette : une alarme en cours dont l'écran ne sait pas lire l'état. */
        etatInconnuAlarme:
            "Une alarme est en cours, son état n'a pas pu être lu.",
        /** S:5072 */
        infoDepuis: "Depuis {quand}, pour information : rien à acquitter",
        /** S:5073 */
        seuil: "Seuil {seuil}.",
        /** S:5078 */
        aucuneMesureDepuis: "Aucune mesure depuis {duree}",
        /** [PROPOSITION] la durée sans mesure n'est pas servie. */
        aucuneMesure: "Aucune mesure reçue",
        /** S:5079 */
        activeDepuisNonAcquittee:
            "Alarme active depuis {quand}, non acquittée.",
        /** S:5080, S:5087 */
        rappel: "Rappel {n} sur {max}.",
        /** S:5087 */
        pasDeRappel: "Pas de rappel automatique.",
        /** S:5085 */
        activeDepuisPersonne:
            "Active depuis {quand}, personne ne l'a acquittée",
        /** S:5086 */
        seuilAuDeclenchement: "Seuil au déclenchement : {seuil}.",
        /** S:5091 */
        acquitteePar: "Acquittée par {nom} {quand}",
        /** [PROPOSITION] la personne n'est pas nommée par la réponse. */
        acquittee: "Acquittée {quand}",
        /** S:5092 */
        activeDepuisSeuil:
            "Alarme active depuis {quand}, seuil au déclenchement : {seuil}.",
        /** [PROPOSITION] le seuil n'est pas servi. */
        activeDepuis: "Alarme active depuis {quand}.",
        /** S:5096 */
        revenueNonAcquittee:
            "Revenue à la normale {quand}, personne ne l'a acquittée",
        /** S:5097-5098 */
        auPlus: "Au plus {valeur} {quand}, hors seuil {duree}.",
        /** S:5102 */
        normaleDepuis: "Normale. En service depuis le {date}",
        /** [PROPOSITION] la date de mise en service n'est pas servie. */
        normale: "Normale.",
        /** S:5108 */
        enAttente: "Alarme en attente.",
        /** S:5109 */
        enAttenteSuite:
            "Elle se mettra en service seule quand l'équipement aura assez de données.",
        /** S:5113 */
        aucuneMesureRecue: "En service, aucune mesure reçue : {motif}",
        /** S:5113 */
        nePeutPasPrevenir: "En service, ne peut pas prévenir : {motif}",
        /** [PROPOSITION] motif inconnu de l'écran. */
        nePeutPasPrevenirSeul: "En service, ne peut pas prévenir.",
        /** S:5116 */
        desactiveeLe: "Désactivée le {date}",
        /** [PROPOSITION] la date n'est pas servie. */
        desactiveeSeul: "Désactivée",
        /** S:5120 */
        motifSuffixe: ", {motif}",
        /** Instant d'un fait : « le 02/10 à 02h03 ». [PROPOSITION] */
        instant: "le {jour} à {heure}",
    },

    /** S:2400-2407 ; `en_attente_historique` est le code que le service écrit. */
    motifsEtat: {
        en_attente_premiere_mesure: "en attente de la première mesure",
        source_muette: "plus rien n'arrive",
        aucun_destinataire: "plus aucun destinataire",
        donnee_sans_ecrivain: "la collecte de cet équipement est arrêtée",
        abonnement_perdu: "abonnement perdu",
        ecart_en_attente: "il faut 90 jours de données sur l'année écoulée",
        en_attente_historique:
            "il faut 90 jours de données sur l'année écoulée",
    },

    /** S:2392-2398 */
    motifsDesactivation: {
        ne_sert_plus: "ne sert plus",
        equipement_retire: "équipement retiré",
        doublon: "doublon",
        erreur_de_declaration: "erreur à la création",
        autre: "autre",
    },

    /** La dernière mesure (`ligneValeur`, S:5168-5178). */
    mesure: {
        /** Une absence de mesure, jamais 0. */
        absente: "—",
        /** [PROPOSITION] la raison de l'absence, lue par un lecteur d'écran. */
        absenteRaison: "Mesure non disponible",
        /** S:5173 */
        aucuneMesure: "Aucune mesure",
        /** S:5173 */
        derniereMesure: "dernière mesure {quand}",
        /** [PROPOSITION] un seuil bas et un seuil haut. */
        entre: "entre {bas} et {haut}",
    },

    /** L'onglet « Surveillance » d'un équipement (lot F3). */
    equipement: {
        /** [PROPOSITION] */
        intro: "Les surveillances de cet équipement, déjà triées : les alarmes à traiter d'abord.",
        /** [PROPOSITION] */
        vide: "Aucune surveillance sur cet équipement pour le moment.",
        /** S:1464, le lien vers la fiche depuis le nom de l'équipement (S:1459). [PROPOSITION] */
        ouvrirLaFiche: "Ouvrir la fiche de la surveillance",
    },

    /** La fiche d'une surveillance (écran 2, `rendreFiche` S:5563-5640). */
    fiche: {
        /** [PROPOSITION] */
        chargement: "Lecture de la surveillance en cours.",
        /** [PROPOSITION] la surveillance n'est pas servie sur cet équipement. */
        introuvable:
            "Cette surveillance n'est pas visible pour votre compte sur cet équipement.",
        /** [PROPOSITION] d'après `alarmes_indisponible`, pour une lecture. */
        indisponible:
            "Cette surveillance ne peut pas être lue pour le moment. Elle pourra être relue dans quelques instants.",
        /** [PROPOSITION] */
        versLaListe: "Toutes les surveillances de l'équipement",
        /** S:5593 dit « Situation courante » ; « situation » est un mot de travail (lexique v1.1). [PROPOSITION] */
        situation: "En ce moment",
        /** S:5583 */
        voirLAlarme: "Voir l'alarme",
        /** S:5583 */
        voirLInformation: "Voir l'information",
        /** [PROPOSITION] lue en entier, aucune alarme ouverte. */
        aucuneAlarme: "Aucune alarme en cours.",
        /** S:5595, la valeur de l'alarme en cours. [PROPOSITION] */
        derniereMesure: "Dernière mesure {valeur}, {quand}.",
        /** S:5601 */
        aTraiter: "À traiter",
        /** S:5601, sans le mot interne. */
        aTraiterTexte:
            "quelqu'un doit s'en occuper, même une fois revenue à la normale.",
        /** S:5600 */
        information: "Information",
        /** S:5600 */
        informationTexte:
            "un mail au début et un à la fin, sans bouton d'acquittement, sans rappel, pas de SMS par défaut.",
        /** S:5631 */
        parametres: "Paramètres",
        /** [PROPOSITION] un réglage et sa valeur. */
        parametre: "{libelle} : {valeur}.",
        /** [PROPOSITION] d'après S:5609-5610. */
        parametreParEtat: "{libelle} : {valeurs}.",
        /** S:5609 */
        siteOuvert: "site ouvert {valeur}",
        /** S:5609 */
        siteFerme: "site fermé {valeur}",
        /** [PROPOSITION] */
        parametresNonDisponibles:
            "Les paramètres de cette surveillance ne sont pas disponibles.",
        /** [PROPOSITION] un réglage que l'écran ne sait pas nommer. */
        parametresNonAffiches:
            "D'autres paramètres de cette surveillance ne peuvent pas être affichés.",
        /** S:5607 */
        horaires: "Horaires du site",
        /** S:5612 */
        horairesSuivis:
            "Elle suit seule les horaires de son site : quand le site en reçoit, elle prend site fermé la valeur que propose son type d'alarme ; s'ils sont retirés, elle revient à sa valeur site ouvert.",
        /** S:5614, réduit à la lecture. [PROPOSITION] */
        horairesNonPris:
            "Les horaires du site ont changé et ne sont pas parvenus à cette surveillance : elle juge avec les précédents.",
        /** S:5635 */
        consigne: "Consigne de votre équipe :",
        /** S:5620 */
        consigneEcrite: "« {texte} », écrite par {nom} le {date}",
        /** [PROPOSITION] la date n'est pas servie. */
        consigneEcriteSansDate: "« {texte} », écrite par {nom}",
        /** S:5620 */
        consigneAucune: "aucune",
        /** S:5138 */
        astreinteUne: "astreinte incomplète : {noms} n'a pas de numéro",
        /** S:5138 */
        astreintePlusieurs: "astreinte incomplète : {noms} n'ont pas de numéro",
        /** [PROPOSITION] un autre motif d'astreinte incomplète. */
        astreinte: "astreinte incomplète",
        /** S:5637 */
        vie: "Vie de la surveillance",
        /** S:5550 */
        vieMiseEnService: "Mise en service",
        /** [PROPOSITION] */
        vieMiseEnServiceTexte: "Elle surveille et prévient.",
        /** S:5556 */
        vieDesactivation: "Désactivation",
        /** S:5556 */
        vieDesactivationPar: "Par {nom}, motif : {motif}.",
        /** S:5556 */
        vieDesactivationParSansMotif: "Par {nom}, sans motif.",
        /** [PROPOSITION] l'auteur n'est pas servi. */
        vieDesactivationMotif: "Motif : {motif}.",
        /** [PROPOSITION] */
        vieDepuis: "depuis {quand}",
        /** [PROPOSITION] */
        historique: "Les alarmes de cette surveillance",
        /** [PROPOSITION] */
        historiqueVide: "Aucune alarme sur cette surveillance.",
        /** [PROPOSITION] la lecture s'est arrêtée au plafond de pages. */
        historiqueIncomplet:
            "Les alarmes suivantes n'ont pas pu être lues : cette liste n'est pas complète.",
        /** [PROPOSITION] */
        historiqueIndisponible:
            "Les alarmes de cette surveillance ne peuvent pas être lues pour le moment.",
        /** [PROPOSITION] une alarme terminée. */
        close: "Close",
        /** [PROPOSITION] */
        ouverteLe: "Ouverte {quand}",
    },

    /** Le détail d'une alarme (écran 7, `rendreEpisode` S:7675-7799). */
    detail: {
        /** [PROPOSITION] */
        chargement: "Lecture de l'alarme en cours.",
        /** [PROPOSITION] d'après `alarmes_indisponible`, pour une lecture. */
        indisponible:
            "Le détail de cette alarme ne peut pas être lu pour le moment. Il pourra être relu dans quelques instants.",
        /** [PROPOSITION] */
        versLaFiche: "Voir la fiche de la surveillance",
        /** S:7695 */
        information: "Information",
        /** S:7707 */
        derniereMesure: "Dernière mesure",
        /** S:7705 */
        fait: "Fait",
        /** S:7705 */
        defautMesure:
            "Défaut de mesure : aucune mesure depuis {duree}, dernière mesure {quand}",
        /** [PROPOSITION] une durée ou une heure manque. */
        defautMesureSeul: "Défaut de mesure : aucune mesure reçue",
        /** S:7710, D124 */
        seuil: "Seuil au déclenchement",
        /** S:7710 */
        seuilValeur: "{seuil}, celui du déclenchement",
        /** S:7710 */
        seuilSiteFerme: "{seuil}, site fermé, celui du déclenchement",
        /** S:7715 */
        activeDepuis: "Active depuis",
        /** S:7715 */
        ouverte: "Ouverte",
        /** S:7716 */
        dureeHorsSeuil: "Durée hors seuil",
        /** S:7717 */
        dureeSansMesure: "Durée sans mesure",
        /** S:7716 */
        aucune: "aucune",
        /** S:7718 */
        revenue: "Revenue à la normale",
        /** S:7719 */
        acquittement: "Acquittement",
        /** S:7719 */
        acquittee: "acquittée",
        /** S:7719 */
        nonAcquittee: "non acquittée",
        /** S:7719 dit « sans objet » (mot de travail, lexique v1.1) ; « rien à acquitter » vient de S:7703. */
        sansObjet:
            "rien à acquitter : c'est une information, elle se clôt seule une fois revenue à la normale",
        /** S:7721 */
        acquitteePar: "Acquittée par",
        /** S:7721-7722 */
        acquitteeParValeur: "{nom} {quand} ({canal})",
        /** S:7722 */
        canalLien: "par le lien du message",
        /** S:7722 */
        canalEcran: "depuis l'écran",
        /** S:7787 */
        closeTitre: "Alarme close",
        /** S:7787 */
        closeVue: "Vue à l'ouverture du détail par {nom} {quand}.",
        /** [PROPOSITION] close sans lecteur nommé. */
        closeLe: "Close {quand}.",
        /** S:7783 */
        vueTitre: "Ouvrir ce détail la marque vue",
        /** S:7784, sans la phrase du bouton (aucun bouton ajouté). */
        vueTexte:
            "Cette alarme est revenue à la normale et personne ne l'a acquittée. Ouvrir son détail la marque « vue » et la clôt ; la clôture nomme le lecteur et l'instant.",
        /** S:7791 */
        prevenus: "Qui a été prévenu, par quel canal, avec quel résultat",
        /** S:7752 */
        colonnes: {
            evenement: "Événement",
            heure: "Heure",
            canal: "Canal",
            personne: "Personne",
            resultat: "Résultat",
        },
        /** [PROPOSITION] */
        aucunEnvoi: "Aucun envoi pour cette alarme.",
        /** S:7792 */
        journal: "Journal d'événements",
        /** S:7793 */
        journalAide:
            "C'est la réponse à « pourquoi celle-là n'est pas partie », sans qu'un humain ait à ouvrir la base.",
        /** [PROPOSITION] */
        journalVide: "Aucun événement pour cette alarme.",
        /** [PROPOSITION] le journal a d'autres lignes, non lues. */
        journalIncomplet:
            "Le journal de cette alarme compte d'autres lignes, qui ne sont pas affichées ici.",
        /** S:7764 */
        horsFenetre: "après la fenêtre de la courbe, marqueur au bord droit",
    },

    /** La courbe d'une alarme (`courbeSVG` et `resumeCourbe`, S:7057-7214). */
    courbe: {
        /** S:7060 */
        aucunPoint:
            "Aucune mesure n'est arrivée sur cette période : rien n'est dessiné.",
        /** S:7209 */
        resumeDerniere: "{valeur} à la dernière mesure",
        /** S:7210 */
        resumeHaut: ", {valeur} au plus haut {quand}",
        /** S:7210 */
        resumeBas: ", {valeur} au plus bas {quand}",
        /** S:7211 */
        resumeHorsSeuil: ", hors seuil depuis {duree}",
        /** S:7212 */
        resumeTrous: ", pas de mesure entre {liste}",
        /** S:7212 */
        resumeTrou: "{debut} et {fin}",
        /** S:7212 */
        resumeTrouSuivant: ", ni entre ",
        /** [PROPOSITION] la cadence n'est pas servie : aucun trou n'est supposé. */
        resumeCadenceInconnue:
            ", points non reliés : la cadence des mesures n'est pas connue",
        /** S:7213 */
        resumeFin: ".",
        /** S:7138 */
        seuil: "seuil {valeur}",
        /** S:7144 */
        pasDeMesure: "pas de mesure",
        /** S:7123 */
        bandeDepassement: "dépassement",
        /** S:7123 */
        bandeActive: "alarme active",
        /** S:7123 */
        bandeNormale: "normale",
        /** S:7730-7737 */
        legende: {
            depassement: "dépassement, pas encore d'alarme",
            active: "alarme active",
            revenue: "revenue à la normale",
            sansBande: "sans bande : normale, en service",
            horsSeuil: "côté hors seuil",
            hachures: "hachures : pas de mesure",
            evenement: "événement, même numéro au journal",
        },
    },

    /** Le journal d'une alarme, une phrase par type de ligne (lot F3). */
    journal: {
        titres: {
            /** S:5553 */
            ouverture: "Alarme active",
            /** S:5553 */
            ouvertureInformation: "Information ouverte",
            /** [PROPOSITION] */
            close: "Alarme close",
            /** D122 */
            acquittement: "Acquittée",
            /** S:2271 */
            normale: "Revenue à la normale",
            /** S:2205 */
            envoi: "Envoi de l'alarme",
            /** S:2272 */
            envoiFin: "Message de fin",
            /** S:6964 */
            envoiTest: "Message de test",
            /** S:2230 */
            rappel: "Rappel {n}",
            /** S:2230 */
            rappelSur: "Rappel {n} sur {max}",
            /** [PROPOSITION] */
            retenu: "Envoi non parti",
            /** [PROPOSITION] */
            arret: "Rappels arrêtés",
            /** [PROPOSITION] */
            remiseNonConfirmee: "Remise non confirmée",
            /** [PROPOSITION] */
            lienOuvert: "Lien du message ouvert",
            /** [PROPOSITION] */
            etat: "Changement d'état de la surveillance",
            /** S:2360 */
            avis: "Réponse à la question",
            /** [PROPOSITION] un type de ligne que l'écran ne sait pas dire. */
            inconnu: "Événement non disponible",
        },
        textes: {
            /** S:2204, réduit. */
            ouvertureDepassement:
                "Hors seuil assez longtemps, en mesures effectivement reçues, pour prévenir.",
            /** S:2313, réduit. */
            ouvertureDefautMesure:
                "Défaut de mesure : plus aucune mesure n'arrive, les destinataires sont prévenus comme pour un dépassement.",
            /** S:7787 */
            closeVue: "Vue à l'ouverture du détail par {nom}.",
            /** [PROPOSITION] */
            closeNormale: "Close une fois revenue à la normale.",
            /** [PROPOSITION] */
            close: "L'alarme est close.",
            /** S:7721-7722 */
            acquitteeEcran: "Par {nom}, depuis l'écran.",
            /** S:7721-7722 */
            acquitteeLien: "Par {nom}, par le lien du message.",
            /** [PROPOSITION] */
            acquittee: "Par {nom}.",
            /** [PROPOSITION] */
            normale: "La mesure est revenue dans le seuil.",
            /** S:2205 */
            envoiVers: "{canal} à {nom}.",
            /** S:2231 */
            nonEnvoyeVers: "{canal} non envoyé à {nom} : {raison}.",
            /** S:2231 */
            nonEnvoye: "Non envoyé : {raison}.",
            /** [PROPOSITION] */
            remiseNonConfirmee:
                "{canal} à {nom} : accepté, sa remise n'a pas été confirmée.",
            /** [PROPOSITION] */
            lienOuvertPar: "Ouvert par {nom}.",
            /** [PROPOSITION] */
            etatVers: "{etat}.",
            /** [PROPOSITION] */
            etatVersMotif: "{etat} : {motif}.",
            /** S:2360 */
            avisUtile: "« oui, utile » à « cette alerte était-elle utile ? ».",
            /** S:2360 */
            avisPasUtile:
                "« non, pas utile » à « cette alerte était-elle utile ? ».",
            /** [PROPOSITION] */
            arretNb: "{n} rappels envoyés.",
        },
        /** Pourquoi les rappels se sont arrêtés. [PROPOSITION] */
        arrets: {
            prise_en_compte: "L'alarme a été acquittée.",
            retour_normal: "L'alarme est revenue à la normale.",
            instance_desactivee: "La surveillance a été désactivée.",
            instance_non_surveillee: "La surveillance ne peut plus prévenir.",
            source_muette: "Plus aucune mesure n'arrive.",
        },
        /** Pourquoi un envoi a été retenu. [PROPOSITION] */
        retenues: {
            condition_non_remplie:
                "la condition pour prévenir n'était pas remplie",
            non_evaluable: "la mesure ne permettait pas de juger",
            en_veille: "la surveillance était en veille",
            non_surveillee: "la surveillance ne pouvait pas prévenir",
            desactivee: "la surveillance était désactivée",
            aucun_destinataire: "personne n'était à prévenir",
            retenue_regroupement_ou_plafond:
                "regroupé avec un message précédent, ou plafond de messages atteint",
        },
    },

    /** Les envois d'une alarme (lot F3). */
    envois: {
        /** S:2197 */
        canaux: {
            sms: "SMS",
            mail: "Mail",
        },
        /** [PROPOSITION] */
        canalInconnu: "Canal non disponible",
        /** Hors maquette : un identifiant absent de `personnes` n'a pas de nom. [PROPOSITION] */
        personneInconnue: "une personne de l'équipe",
        /** [PROPOSITION] */
        personneInconnueDebut: "Une personne de l'équipe",
        /** S:2222-2225 */
        prefixeRappel: "rappel {n}, ",
        /** S:2222-2225 */
        prefixeRappelSur: "rappel {n} sur {max}, ",
        /** S:2265 */
        prefixeFin: "message de fin, ",
        /** [PROPOSITION] */
        prefixeTest: "message de test, ",
        resultats: {
            /** S:2197 */
            remis: "remis {quand}",
            /** [PROPOSITION] un mail ouvert par son destinataire. */
            ouvert: "ouvert {quand}",
            /** [PROPOSITION] */
            remisSansHeure: "remis",
            /** [PROPOSITION] */
            accepte: "envoyé, remise en attente",
            /** [PROPOSITION] */
            remiseNonConfirmee: "envoyé, remise non confirmée",
            /** [PROPOSITION] */
            enCours: "en cours d'envoi",
            /** S:2222 */
            nonRemis: "non remis",
            /** S:2224 */
            nonEnvoye: "non envoyé : {raison}",
            /** [PROPOSITION] */
            inconnu: "résultat non disponible",
        },
        /** Pourquoi un canal n'est pas parti (`motif_non_envoi_canal`, T1 §2 et D96). */
        raisons: {
            /** S:6721 (SMS) ; [PROPOSITION] pour le mail */
            aucune_adresse_sur_le_canal_sms: "pas de numéro",
            aucune_adresse_sur_le_canal_mail: "pas d'adresse mail",
            /** D127 (SMS) ; [PROPOSITION] pour le mail */
            adresse_non_conforme_sms: "numéro à corriger",
            adresse_non_conforme_mail: "adresse mail à corriger",
            /** [PROPOSITION] */
            plus_membre_de_lentreprise: "ne fait plus partie de l'entreprise",
            /** [PROPOSITION] */
            expediteur_non_enregistre:
                "l'envoi n'est pas encore ouvert sur ce canal, ce n'est pas une panne",
            /** [PROPOSITION] */
            plafond_atteint: "plafond de messages du jour atteint",
            /** S:6997 */
            destinataire_hors_liste_blanche:
                "ce numéro n'est pas encore autorisé, ce n'est pas une panne",
            /** S:4929 */
            module_sms_non_souscrit: "votre offre ne comprend pas les SMS",
            /** S:4929 */
            solde_sms_epuise: "solde de SMS épuisé",
            /** [PROPOSITION] un motif que l'écran ne sait pas dire. */
            inconnue: "raison non disponible",
        },
    },

    /**
     * Les actions sur une surveillance, une par une (lot F6). Les textes sans
     * ligne de la maquette sont marqués [PROPOSITION].
     */
    actions: {
        /** S:4895 */
        acquitter: "Acquitter",
        /** S:5209, S:5293 */
        aideAcquitter:
            "Acquitter, c'est dire « je m'en occupe » : les rappels s'arrêtent pour tout le monde.",
        /** S:5575 */
        desactiver: "Désactiver",
        /** S:5574 */
        reactiver: "Réactiver",
        /** S:5576 */
        changerDestinataires: "Changer les destinataires",
        /** S:5577 */
        modifierParametres: "Modifier les paramètres",
        /** S:5582 */
        inscrire: "M'inscrire, par mail",
        /** S:5582 */
        retirer: "Me retirer",
        /** [PROPOSITION] le geste 15 n'a pas de bouton dans la maquette de la fiche. */
        ecrireConsigne: "Écrire la consigne",
        /** [PROPOSITION] */
        modifierConsigne: "Modifier la consigne",
        /** S:5585-5588 */
        aide: {
            desactiver:
                "Désactiver : elle ne surveille plus et ne prévient plus personne. Elle reste dans la liste, avec qui l'a arrêtée et quand. Une alarme active se clôt sans être revenue à la normale.",
            reactiver:
                "Réactiver : elle repart comme neuve, sans rouvrir d'alarme.",
            parametres:
                "Modifier les paramètres : le nouveau seuil vaut pour la suite ; une alarme active garde le seuil de son déclenchement.",
            destinataires:
                "Changer les destinataires : qui est prévenu, et par quel canal.",
        },
        /** S:5338 */
        annuler: "Annuler",
        /** S:5692 */
        enregistrer: "Enregistrer",
        /** S:5475 */
        confirmer: "Confirmer",
        /** S:5427 */
        continuer: "Continuer",
        /** [PROPOSITION] */
        fermer: "Fermer",
        /** [PROPOSITION] pendant l'attente de la réponse ; rien n'a encore changé. */
        envoi: "Envoi en cours, en attente de la réponse.",
        /** [PROPOSITION] la relecture qui suit une réponse. */
        relecture: "Relecture en cours.",
        /** [PROPOSITION] délai dépassé, réseau coupé ou 503 : on ne sait pas. */
        incertain:
            "Nous ne savons pas si la modification a été enregistrée. La surveillance est relue : vérifiez son état avant de recommencer.",
        /** [PROPOSITION] */
        incertainRelu:
            "Nous ne savions pas si la modification avait été enregistrée : la surveillance a été relue, ce qui est affiché est ce qui est enregistré.",
        /** [PROPOSITION] */
        incertainNonRelu:
            "Nous ne savons pas si la modification a été enregistrée, et la surveillance n'a pas pu être relue. Relisez-la avant de recommencer.",
        /** [PROPOSITION] une réponse est arrivée, mais la relecture a échoué. */
        nonRelu:
            "La surveillance n'a pas pu être relue : ce qui est affiché peut ne pas être à jour.",
        /** Ce que dit un succès. */
        fait: {
            /** S:8593 */
            acquitter:
                "Acquittée par {nom} {quand}. Les rappels s'arrêtent pour tout le monde.",
            /** [PROPOSITION] un second appui : le serveur nomme le premier acquitteur. */
            acquitterDeja: "Elle l'était déjà : acquittée par {nom} {quand}.",
            /** [PROPOSITION] */
            desactiver: "Surveillance désactivée.",
            /** [PROPOSITION] */
            reactiver: "Surveillance réactivée.",
            /** S:5718 */
            parametres:
                "Paramètres enregistrés. Ils valent pour les prochaines mesures.",
            /** [PROPOSITION] */
            destinataires: "Destinataires enregistrés.",
            /** S:5509, au singulier */
            aucunDestinataire:
                "Plus personne n'est prévenu : cette surveillance ne peut plus alerter. Ajoutez au moins une personne pour qu'elle prévienne de nouveau.",
            /** S:5655 */
            inscrire:
                "Vous êtes inscrit à cette surveillance, prévenu par mail.",
            /** S:5655 */
            retirer: "Vous n'êtes plus prévenu par cette surveillance.",
            /** [PROPOSITION] */
            consigne: "Consigne enregistrée.",
            /** [PROPOSITION] d'après S:6946 */
            test: "Message de test envoyé à {nom}, par {canal}. Son résultat se lit sous son nom.",
            /** S:8913, sans le numéro */
            numero: "Numéro enregistré pour {nom}.",
            /** S:7624 */
            avis: "Merci, réponse enregistrée : {valeur}.",
        },
        /** Les refus des écritures. */
        refus: {
            /** api-metier `defaultError.js:1278` */
            avance: "Cette fonction fait partie de l'option Avancé.",
            /** api-metier `defaultError.js:1329`, sans le bilan */
            alarmeRevenue:
                "Cette alarme n'est plus en cours : il n'y a plus rien à acquitter.",
            /** api-metier `defaultError.js:1336` */
            pourInformation:
                "Cette alarme est pour information : il n'y a rien à acquitter.",
            /** matrice v1.6, `phrase_condition` du geste 4 */
            pasDestinataire: "Réservé aux destinataires de cette alarme.",
            /** S:6933 */
            capTest:
                "Trop de messages de test aujourd'hui pour cette personne. Réessayez demain.",
            /** [PROPOSITION] */
            destinataireInconnu:
                "Cette personne n'est pas destinataire de cette surveillance.",
            /** [PROPOSITION] */
            canalHorsDestinataire:
                "Cette personne n'est pas prévenue par ce canal sur cette surveillance.",
            /** [PROPOSITION] */
            avisHorsEcart:
                "Cette question ne se pose que sur un écart inhabituel.",
            /** S:5714 */
            valeursInvalides:
                "Cette valeur est hors de la plage de ce type d'alarme : rien n'a été écrit.",
            /** [PROPOSITION] */
            forme: "La demande n'a pas la forme attendue : rien n'a été enregistré.",
            /** [PROPOSITION] */
            numero: "Ce numéro n'a pas été enregistré.",
            /** [PROPOSITION] */
            generique: "La demande a été refusée : rien n'a été enregistré.",
        },
        /** Le dialogue « Désactiver » (S:5320-5343). */
        desactiverDialogue: {
            /** S:5329, au singulier */
            titre: "Désactiver cette surveillance ?",
            /** S:5330 */
            texte: "Une surveillance désactivée ne déclenche plus rien et ne prévient plus personne. Elle reste dans la liste, et se réactive en une fois. Le motif est proposé, jamais exigé.",
            /** S:5332 */
            pourquoi: "Pourquoi ?",
            /** S:5334 */
            ajouterPrecision: "Ajouter une précision",
            /** S:5335 */
            precision: "Précision facultative, jamais interprétée",
            /** S:5336 */
            exemple: "Par exemple : logement rendu le 12/09",
            /** S:5340 */
            sansPreciser: "Désactiver sans préciser",
        },
        /** Le dialogue « Réactiver » (S:5390-5396). */
        reactiverDialogue: {
            /** S:5392, au singulier */
            titre: "Réactiver cette surveillance ?",
            /** S:5393 */
            texte: "Une surveillance réactivée repart comme une surveillance neuve, à partir de la prochaine mesure : aucune alarme n'est rouverte, et son motif de désactivation est vidé dans la même écriture.",
        },
        /** Le dialogue « Modifier les paramètres » (S:5671-5694). */
        parametresDialogue: {
            /** S:5690 */
            titre: "Modifier les paramètres",
            /** S:5690 */
            texte: "Le nouveau seuil vaut pour la suite ; une alarme active garde le seuil de son déclenchement.",
            /** S:5676 */
            siteOuvert: "Site ouvert : {libelle}",
            /** S:5678 */
            siteFerme: "Site fermé : {libelle}",
            /** S:5676 */
            enUnite: "{libelle}, en {unite}",
            /** S:5684 */
            plage: "Entre {min} et {max}.",
            /** S:5679 */
            horaires:
                "Les horaires viennent du site ; ils se changent dans « Horaires du site », pour toutes ses surveillances à la fois.",
            /** [PROPOSITION] */
            illisible: "Saisissez un nombre.",
            /** [PROPOSITION] un seuil posé ne se vide pas ici. */
            vide: "Saisissez une valeur : ce seuil est posé.",
            /** [PROPOSITION] */
            rienAChanger: "Aucun paramètre n'a changé.",
            /** [PROPOSITION] */
            aucunReglable:
                "Aucun paramètre de cette surveillance ne se règle ici.",
        },
        /** Le dialogue « Changer les destinataires » (S:5418-5480). */
        destinatairesDialogue: {
            /** S:5422, au singulier */
            titre: "Changer les destinataires de cette surveillance",
            /** [PROPOSITION] d'après S:5423 */
            texte: "Une personne déjà prévenue garde ses canaux ; une personne ajoutée est prévenue par les canaux que le type d'alarme propose. Le détail par personne se règle depuis la fiche.",
            /** S:5446 */
            prevenu: "Prévenu",
            /** S:5462, au singulier */
            confirmerTitre: "Changer les destinataires de cette surveillance ?",
            /** S:5464 */
            seront: "{noms} seront prévenus.",
            /** S:5464 */
            sera: "{noms} sera prévenu.",
            /** S:5463 */
            personne: "Plus personne ne sera prévenu.",
            /** S:5468, au singulier */
            personneDanger:
                "Cette surveillance restera en service mais ne pourra plus prévenir personne : elle passera en « ne peut pas prévenir ».",
            /** [PROPOSITION] */
            membresIndisponibles:
                "La liste des membres ne peut pas être lue : les destinataires ne peuvent pas être changés pour le moment.",
            /** [PROPOSITION] */
            rienAChanger: "Les destinataires n'ont pas changé.",
            /** [PROPOSITION] */
            nonModifiable:
                "La liste servie n'est pas lisible en entier : elle ne peut pas être changée ici.",
        },
        /** Le dialogue de la consigne de l'équipe (S:6678-6682). */
        consigneDialogue: {
            /** S:6678 */
            titre: "Consigne de votre équipe",
            /** S:6678 */
            facultative: "(facultative)",
            /** S:6680 */
            exemple:
                "Par exemple : appeler le gardien avant 22 h, puis l'astreinte.",
            /** [PROPOSITION] */
            longueur: "{n} caractères sur 400.",
            /** [PROPOSITION] */
            rienAChanger: "La consigne n'a pas changé.",
        },
        /** Se retirer quand on est la dernière personne prévenue (T5 §A.4). */
        inscription: {
            /** [PROPOSITION] */
            dernier:
                "Vous êtes la seule personne prévenue : en vous retirant, plus personne ne sera prévenu, et la surveillance ne pourra plus alerter.",
            /** [PROPOSITION] */
            confirmerRetrait: "Me retirer quand même",
        },
        /** Le message de test (écran 6, S:6925-7000). */
        test: {
            /** S:6976 */
            bouton: "Envoyer un message de test",
            /** [PROPOSITION] */
            titre: "Envoyer un message de test ?",
            /** [PROPOSITION] à qui part le message, et par quel canal. */
            vers: "Un message de test part vers {nom}, par {canal}.",
            /** [PROPOSITION] */
            auNumero: "Au numéro {masque}.",
            /** S:6973 */
            numeroRenseigne: "Au numéro renseigné.",
            /** S:6994 */
            unSms: "1 SMS, déduit du solde.",
            /** S:1537 */
            facultatif: "Le test est facultatif et n'est jamais un préalable.",
            /** [PROPOSITION] */
            canal: "Par quel canal ?",
            /** [PROPOSITION] */
            envoyer: "Envoyer",
            /** [PROPOSITION] */
            sms: "SMS",
            /** [PROPOSITION] */
            mail: "mail",
            /** [PROPOSITION] */
            incertain:
                "Nous ne savons pas si le message de test est parti. Son résultat est relu : lisez-le avant d'en envoyer un autre.",
            /** [PROPOSITION] */
            relectures:
                "Le résultat est relu maintenant, puis deux fois dans la minute.",
        },
        /** Le numéro d'un membre (S:6910-6922). */
        numero: {
            /** S:6982 */
            saisir: "Saisir le numéro",
            /** S:6982 */
            corriger: "Corriger le numéro",
            /** S:6914 */
            titre: "Numéro de {nom}",
            /** S:6915, première phrase */
            texte: "Réservé au propriétaire et à l'administrateur de l'entreprise.",
            /** S:6916 */
            libelle: "Numéro de mobile",
            /** S:6917 */
            exemple: "06 00 00 00 00",
            /** [PROPOSITION] */
            aide: "Un numéro de mobile ; hors de France, avec l'indicatif du pays.",
            /** [PROPOSITION] */
            incertain:
                "Nous ne savons pas si le numéro a été enregistré. La liste des membres est relue : vérifiez l'état du numéro avant de recommencer.",
        },
        /** « Cette alerte était-elle utile ? » (S:7620-7634, D108). */
        avis: {
            /** S:7629 */
            question: "Cette alerte était-elle utile ?",
            /** S:7631 */
            oui: "Oui",
            /** S:7632 */
            non: "Non",
            /** S:7624 */
            utile: "utile",
            /** S:7624 */
            pasUtile: "pas utile",
            /** S:7625 */
            changer: "Changer ma réponse",
            /** [PROPOSITION] */
            incertain:
                "Nous ne savons pas si votre réponse a été enregistrée. Vous pouvez répondre de nouveau : la même réponse n'écrit rien de plus.",
        },
        /** La ligne de l'astreinte sur la fiche (S:5601, geste 32). */
        astreinte: {
            /** S:2863 */
            basique:
                "Avec le Basique : un mail au début et un mail à la fin, ni rappel ni SMS.",
            /** S:5601 */
            avance: "Avec astreinte : rappels jusqu'à ce que quelqu'un acquitte.",
        },
    },

    /** Les refus du serveur (S:2451-2457, défauts d'api-metier). */
    refus: {
        /** S:2452 */
        role: "Votre rôle ne permet pas cette action. Demandez-la à un administrateur de votre entreprise.",
        /** S:2453 */
        perimetre:
            "Ce lieu ne fait pas partie de votre périmètre. Un administrateur peut vous le partager.",
        /** S:2454 */
        sansBatiment:
            "Cet équipement n'est rattaché à aucun bâtiment. Un administrateur peut le rattacher.",
        /** S:2455 */
        neutre: "Cet écran n'est pas accessible avec ce compte.",
        /** [PROPOSITION] d'après `alarmes_indisponible` (api-metier `defaultError.js:1283`), pour une lecture. */
        indisponible:
            "La liste des surveillances ne peut pas être lue pour le moment. Elle pourra être relue dans quelques instants.",
        /** api-metier `defaultError.js:1301`, message neutre. */
        fermee: "Cette fonctionnalité n'est pas disponible pour votre compte.",
        /** [PROPOSITION] */
        relire: "Relire",
    },

    /** Le catalogue des types d'alarme (écran 3, `rendreCatalogue` S:6150-6205), lot F4. */
    catalogue: {
        /** S:1475 */
        titre: "Le catalogue des types d'alarme",
        /** S:6176, sans la phrase libre (lot F10). */
        intro: "Un type d'alarme porte ce qu'il surveille, le type d'équipement sur lequel il se pose, les paramètres que vous pouvez ajuster, et tous les textes que vous lirez. Quand et comment l'alerte part est notre travail, pas le vôtre.",
        /** S:6175 */
        compteTitreUn: "1 type d'alarme au catalogue",
        compteTitre: "{n} types d'alarme au catalogue",
        /** S:6177-6182 */
        natureTitre: "À traiter ou pour information, selon le type d'alarme",
        natureATraiter:
            "quelqu'un doit s'en occuper, même une fois revenue à la normale.",
        natureInformation:
            "un mail au début et un à la fin, sans bouton d'acquittement, sans rappel, jamais de SMS par défaut ; elle se clôt seule une fois revenue à la normale.",
        /** S:6164-6172 */
        rechercher: "Rechercher",
        rechercheAide: "Par exemple : eau chaude, gaz, froid",
        filtreMesure: "Mesure",
        toutesMesures: "Toutes les mesures",
        filtreEquipement: "Type d'équipement",
        tousEquipements: "Tous les types",
        trier: "Trier",
        /** S:6170 */
        triAlpha: "Ordre alphabétique",
        /** [PROPOSITION] le tri par usage de la maquette (S:6168) n'est pas servi. */
        triATraiter: "À traiter d'abord",
        /** S:6200, sans « dont … que vos équipements peuvent mesurer ». */
        affichesUn: "1 type d'alarme affiché sur {total}.",
        affiches: "{n} types d'alarme affichés sur {total}.",
        /** S:6198, sans le renvoi à « Créer » (lot F8). */
        aucunResultat: "Aucun type d'alarme ne répond à cette recherche.",
        /** Plan F4, `types_ecartes`. [PROPOSITION] */
        ecartesUn:
            "1 type d'alarme n'est pas proposé : aucun des compteurs ou capteurs de cet équipement ne le mesure.",
        ecartes:
            "{n} types d'alarme ne sont pas proposés : aucun des compteurs ou capteurs de cet équipement ne les mesure.",
        /** `types_non_rattaches`. [PROPOSITION] */
        nonRattachesUn:
            "1 type d'alarme est proposé sans que nous sachions encore si cet équipement le mesure.",
        nonRattaches:
            "{n} types d'alarme sont proposés sans que nous sachions encore si cet équipement les mesure.",
        /** Compteurs servis `null` (catalogue non filtré sur un équipement). [PROPOSITION] */
        nonFiltre:
            "Tous les types d'alarme publiés, sans filtre sur un équipement.",
        /** Sur un équipement désigné, un compteur servi illisible : on ne sait pas. [PROPOSITION] */
        compteInconnu:
            "Nous n'avons pas pu lire combien de types d'alarme ne sont pas proposés pour cet équipement.",
        /** [PROPOSITION] */
        chargement: "Lecture du catalogue en cours.",
        /** [PROPOSITION] d'après `alarmes_indisponible`, pour une lecture. */
        indisponible:
            "Le catalogue ne peut pas être lu pour le moment. Il pourra être relu dans quelques instants.",
        /** [PROPOSITION] le catalogue publié est vide. */
        vide: "Aucun type d'alarme n'est publié pour le moment.",
        /** [PROPOSITION] sur un équipement, rien de proposé et le compte des écartés illisible. */
        videEquipement:
            "Aucun type d'alarme n'est proposé pour cet équipement.",
        /** Le lien depuis la liste. [PROPOSITION] */
        lien: "Voir le catalogue des types d'alarme",
        /** [PROPOSITION] */
        versLaListe: "Toutes les surveillances",
        carte: {
            /** S:6249 */
            aTraiter: "À traiter",
            aTraiterAstreinte: "À traiter, avec astreinte",
            information: "Pour information",
            /** S:6251, « {mesure} · se pose sur {equipements} ». */
            sePoseSur: "{mesure} · se pose sur {equipements}",
            /** S:6255 */
            siPersonneNAgit: "Si personne n'agit.",
            /** S:6260 */
            details: "Paramètres et mots que vous lirez",
            /** S:6226 */
            rienARegler: "Rien à régler sur ce type d'alarme.",
            /** S:6229-6230 */
            proposee: "Proposé à {valeur}, ajustable entre {min} et {max}.",
            /** [PROPOSITION] une valeur servie sans sa plage. */
            proposeeSansPlage: "Proposé à {valeur}.",
            /** [PROPOSITION] un paramètre servi sans valeur proposée. */
            reglable: "Ajustable à la création de la surveillance.",
            /** S:6263 */
            quand: "Quand vous êtes prévenu",
            /** [PROPOSITION] la phrase du type n'est pas servie. */
            sansPhrase:
                "La description de ce type d'alarme n'est pas disponible.",
            /** [PROPOSITION] */
            nomNonDisponible: "Type d'alarme sans nom",
            /** S:2196 */
            canaux: "Proposé par {canaux}.",
        },
        /** Les types d'équipement (S:1732-1736, `objet_types` servis). [PROPOSITION] pour la source. */
        equipements: {
            building: "bâtiment",
            source: "compteur ou capteur",
        },
        /** [PROPOSITION] un type d'équipement servi que l'écran ne sait pas dire. */
        equipementInconnu: "type d'équipement non précisé",
        /** « ou », entre deux types d'équipement (S:5035). */
        ou: " ou ",
        /** « et », entre deux canaux. */
        et: " et ",
    },

    /**
     * Le nom de la mesure surveillée, par code servi (`variable` du type, alarmes
     * `catalogue/variables.yaml`), d'après les « grandeurs » de la maquette
     * (S:1827-2108). [PROPOSITION] pour le nom ; la chaîne du froid partage la
     * mesure d'un logement, que le catalogue ne distingue pas.
     */
    mesures: {
        temperature_air_interieur: "Température intérieure",
        temperature_eau_departecs: "Température d'eau",
        temperature_eau_retourecs: "Température d'eau",
        energie_elec_tout: "Consommation",
        energie_elecinjection_tout: "Production",
        puissance_elec_apparente: "Puissance",
        puissance_elec_tout: "Puissance",
        energiepardju_gaz_chauffage: "Consommation",
        energie_gaz_tout: "Consommation",
        /** [PROPOSITION] une mesure servie que l'écran ne sait pas nommer. */
        inconnue: "Autre mesure",
    },

    /** « Qui est prévenu, et comment » sur la fiche (S:5533-5546, S:5636), lot F4. */
    prevenus: {
        /** S:5636 */
        titre: "Qui est prévenu, et comment",
        /** S:5545 */
        personne: "Personne",
        sms: "SMS",
        mail: "Mail",
        /** S:5539-5541 */
        oui: "oui",
        non: "non",
        /** S:5539 */
        cocheNonEnvoye: "coché, non envoyé",
        /** S:5540 */
        sansNumero: "sans numéro",
        /** S:5535 */
        personneNEstPrevenu:
            "Personne n'est prévenu : cette surveillance ne peut pas alerter.",
        /** [PROPOSITION] la surveillance ne dit pas qui elle prévient. */
        nonDisponible: "La liste des personnes prévenues n'est pas disponible.",
        /** Brief F4 : un destinataire absent des membres reste sans nom. [PROPOSITION] */
        horsDesMembres:
            "Personne qui ne figure pas parmi les membres de l'entreprise",
        /** [PROPOSITION] la liste des membres n'a pas pu être lue. */
        membresIndisponibles:
            "Les noms et les numéros ne peuvent pas être lus pour le moment.",
        /** [PROPOSITION] */
        chargementMembres: "Lecture des membres en cours.",
        /** Les rôles, mot court de la matrice des accès (`axes.roles[].court`, S:`libelleRole`). */
        roles: {
            owner: "Propriétaire",
            administrateur: "Administrateur",
            employe: "Employé",
            employe_read_only: "Lecteur",
        },
    },

    /** L'état du numéro d'un membre (S:1727, S:6970, D127), lot F4. */
    numero: {
        /** S:6970 */
        renseigne: "numéro renseigné",
        /** S:1727 */
        sansNumero: "sans numéro",
        /** S:1727, D127 */
        aCorriger: "numéro à corriger",
        /** [PROPOSITION] l'état du numéro n'est pas servi. */
        inconnu: "numéro non disponible",
    },

    /** La relecture des messages de test (écran 6, `rendreTest` S:6964-7000), lot F4. */
    tests: {
        /** S:6976 */
        relire: "Relire le résultat",
        /** [PROPOSITION] */
        chargement: "Lecture des messages de test en cours.",
        /** [PROPOSITION] la lecture a abouti, sans aucun message de test. */
        aucun: "Aucun message de test pour cette personne.",
        /** [PROPOSITION] d'après `alarmes_indisponible`, pour une lecture. */
        indisponible:
            "Le résultat ne peut pas être lu pour le moment. Il pourra être relu dans quelques instants.",
        /** [PROPOSITION] « SMS, demandé le 5 oct. à 10h00 : … ». */
        ligne: "{canal}, demandé {quand} : {resultat}",
        /** [PROPOSITION] sans heure de demande. */
        ligneSansHeure: "{canal} : {resultat}",
        resultats: {
            /** S:6930 « Reçu à 14h32. » */
            remis: "Reçu {quand}.",
            /** [PROPOSITION] */
            remisSansHeure: "Reçu.",
            /** [PROPOSITION] un mail ouvert. */
            ouvert: "Ouvert {quand}.",
            /** S:6929 */
            attente: "Envoyé, en attente de confirmation.",
            /** [PROPOSITION] */
            remiseNonConfirmee: "Envoyé, remise non confirmée par l'opérateur.",
            /** [PROPOSITION] */
            enCours: "En cours d'envoi.",
            /** S:6931 « Non remis : numéro inconnu de l'opérateur. » */
            nonRemis: "Non remis : {motif}.",
            /** S:6932 « Test non envoyé : ce numéro n'est pas encore autorisé. » */
            nonEnvoye: "Test non envoyé : {raison}.",
            /** [PROPOSITION] un statut servi que l'écran ne sait pas lire. */
            inconnu: "Résultat non disponible.",
        },
        /**
         * Les motifs d'échec de l'émetteur (api-metier `MOTIFS_DE_L_EMETTEUR`) et
         * le motif neutre `incident_d_envoi`. S:6931 pour `hard_bounce` en SMS ;
         * les autres [PROPOSITION].
         */
        motifs: {
            soft_bounce_sms: "téléphone momentanément injoignable",
            soft_bounce_mail: "boîte mail momentanément injoignable",
            deferred: "remise reportée par l'opérateur",
            error: "erreur de l'opérateur",
            hard_bounce_sms: "numéro inconnu de l'opérateur",
            hard_bounce_mail: "adresse mail inconnue",
            blocked: "envoi bloqué par l'opérateur",
            spam: "message classé comme indésirable",
            invalid_email: "adresse mail invalide",
            unsubscribed: "la personne s'est désabonnée de ces messages",
            rejected: "message refusé par l'opérateur",
            incident_d_envoi: "incident lors de l'envoi",
            /** Un motif que l'écran ne sait pas dire. */
            inconnu: "motif non disponible",
        },
    },

    /** L'écran 9, « Option Avancé et SMS » (lot F5), en lecture. */
    option: {
        /** [PROPOSITION] le titre de la page et du lien depuis la liste. */
        titre: "Option Avancé et SMS",
        /** [PROPOSITION] le lien depuis la page « Surveillance ». */
        lien: "Option Avancé et SMS",
        /** [PROPOSITION] */
        intro: "Ce que comprend chaque niveau, l'offre de votre entreprise et votre solde de SMS.",

        niveaux: {
            /** S:5803 */
            titre: "Deux niveaux",
            /** S:5803 */
            sousTitre:
                "le basique est compris dans votre offre, l'avancé est une option",
            colonnes: {
                /** S:5804 */
                fonction: "Ce que vous pouvez faire",
                basique: "Basique, compris dans votre offre",
                avance: "Option Avancé",
            },
            /** S:5806-5807 */
            oui: "oui",
            /** S:5806 */
            non: "non",
            /**
             * Les lignes du tableau, dans l'ordre de la maquette (`OFFRE`,
             * champ `client`, S:5726-5743, ordonnées par `ordreOffre`) ; le
             * niveau de chacune est celui de la matrice des accès v1.6.
             */
            lignes: {
                creer: "Créer une surveillance, équipement par équipement, depuis le catalogue ou une phrase",
                fiche: "Fiche d'une surveillance : désactiver, réactiver, modifier, changer les destinataires",
                messages:
                    "Mail au début et à la fin, page du lien, acquittement, journal d'événements",
                horaires:
                    "Horaires du site : semaine, jours fériés, vacances, fermetures datées",
                ecart: "Écart inhabituel de consommation, pour information",
                plusieurs:
                    "Créer plusieurs surveillances en une fois, et les actions groupées",
                fermerSites: "Fermer plusieurs sites en une fois",
                rappels: "Rappels d'astreinte sur la chaîne du froid",
                /** S:5741, sans « que vous rechargez » : la recharge attend P23. */
                sms: "SMS et SMS de fin, sur un volume prépayé",
                test: "Message de test, pour vérifier un numéro",
                recapitulatif:
                    "Récapitulatif quotidien ou hebdomadaire, choisi par chaque destinataire, et son résumé de trois phrases",
            },
            /** S:5810-5813 */
            basiqueTerme: "Basique",
            basiqueTexte: "compris dans votre offre Energy Manager",
            avanceTerme: "Option Avancé",
            avanceTexte:
                "un abonnement mensuel, selon la taille de votre offre Energy Manager",
            smsTerme: "SMS prépayés",
            /** S:5812, sans « que vous rechargez vous-même » : la recharge attend P23. */
            smsTexte: "par volume",
            /** S:5811-5812 */
            tarif: "(tarif communiqué par votre interlocuteur commercial)",
            essaiTerme: "Essai",
            essaiTexte: "30 jours, sur demande au support",
        },

        offre: {
            /** S:5823 */
            basique: "Votre offre : Basique",
            /** S:5823 */
            basiqueSousTitre: "tout ce qui est marqué « Basique »",
            /** S:5824, première phrase seule. */
            basiqueTexte:
                "Vos alarmes partent par mail, s'affichent sur la page ouverte par le lien et s'écrivent au journal d'événements.",
            /**
             * S:5825, sans le bouton : aucune route ne transmet la demande ; et
             * sans « et vous prévient par mail », que D126 ne dit pas.
             */
            essaiPhrase:
                "Pour essayer l'option Avancé 30 jours, demandez-le au support : il ouvre l'essai pour votre entreprise.",
            /** [PROPOSITION] */
            avance: "Votre offre : Option Avancé",
            /** [PROPOSITION] d'après « Option Avancé à l'essai jusqu'au … » (plan F5). */
            essaiEnCours: "Option Avancé à l'essai jusqu'au {date}",
            /** [PROPOSITION] une fin d'essai servie mais illisible. */
            essaiSansDate:
                "Option Avancé à l'essai, date de fin non disponible",
            /** [PROPOSITION] `/offre` n'a pas pu être lue. */
            nonLue: "L'offre de votre entreprise n'a pas pu être lue pour le moment.",
            /** [PROPOSITION] */
            chargement: "Lecture de l'offre en cours.",
        },

        solde: {
            /** S:5833 */
            titre: "Solde de SMS",
            /** S:5834 */
            restants: "SMS restants",
            /** S:5835, sans la phrase sur la recharge (aucune recharge en libre-service). */
            epuiseTitre: "Solde épuisé",
            epuiseTexte:
                "Les SMS ne partent plus ; les mails continuent, et le journal de chaque alarme dit « SMS non envoyé : solde épuisé ».",
            /** S:4929 */
            nonSouscrit: "Votre offre ne comprend pas les SMS.",
            /** Plan §2.4 point 6 : un solde inconnu ne se dit jamais 0. */
            nonDisponible: "Solde non disponible",
            /** [PROPOSITION] */
            chargement: "Lecture du solde en cours.",
        },

        mouvements: {
            /** [PROPOSITION] S:5841 dit « Recharges » ; la route sert aussi les envois. */
            titre: "Mouvements du solde",
            colonnes: {
                /** S:5841 */
                date: "Date",
                /** [PROPOSITION] */
                mouvement: "Mouvement",
                /** [PROPOSITION] */
                sms: "SMS",
                /** [PROPOSITION] */
                soldeApres: "Solde après",
            },
            sens: {
                /** [PROPOSITION] `credit` du service. */
                credit: "Recharge",
                /** [PROPOSITION] `debit` du service. */
                debit: "Envoi",
                /** [PROPOSITION] `regularisation` du service. */
                regularisation: "Régularisation",
                /** [PROPOSITION] un sens que l'écran ne sait pas dire. */
                inconnu: "Mouvement non reconnu",
            },
            /** [PROPOSITION] la route a servi une liste vide. */
            aucun: "Aucun mouvement pour le moment.",
        },
    },

    /** L'écran hors offre (S:8362-8367). */
    horsOffre: {
        titre: "La surveillance fait partie de l'offre Energy Manager",
        texte: "Cette fonction n'est pas comprise dans votre abonnement. Contactez votre interlocuteur commercial.",
    },

    /** Les unités (S:1729). */
    unites: {
        degre_celsius: "°C",
        kilowattheure: "kWh",
        kilowatt: "kW",
        kilovoltampere: "kVA",
        ppm: "ppm",
        pourcent: "%",
        kwh_par_dju: "kWh par degré-jour",
    },

    /** Les durées (`duree`, S:2892-2899). */
    durees: {
        jours: "{n} jours",
        minutes: "{n} minutes",
        heure: "1 heure",
        heures: "{n} heures",
        heuresMinutes: "{h} h {mm}",
    },
} as const

/** La forme des messages du module. */
export type MessagesAlarmesV2 = typeof MESSAGES_ALARMES_V2
